import { PageHeader } from '@ant-design/pro-layout'
import { Button, Form, type ModalFuncProps } from 'antd'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'
import { useRecoilState } from 'recoil'

import type { RSheetColumn, RSheetRecordNode } from '../../../../domain/column'
import { configAtom } from '../../../../recoil/viewConfig'
import { DynamicSelectOptionSqlEditor } from '../../../../rsheet/components/editor/DynamicSelectOptionSqlEditor'

export const buildDynamicSelectOptionModalProperties = ({
  column,
  onAfterFinish,
  setHasChangeToViewSchema,
}: {
  column: RSheetColumn<RSheetRecordNode>
  onAfterFinish: () => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  title: null,
  width: '90%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  content: (
    <ModalContent column={column} onAfterFinish={onAfterFinish} setHasChangeToViewSchema={setHasChangeToViewSchema} />
  ),
})

const ModalContent = ({
  column,
  onAfterFinish,
  setHasChangeToViewSchema,
}: {
  column: RSheetColumn<RSheetRecordNode>
  onAfterFinish: () => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const [loading, setLoading] = useState(false)
  const [config, setConfig] = useRecoilState(configAtom)

  const title = t(`「{{columnName}}」の連動項目`, { columnName: column.title })

  return (
    <PageHeader title={title}>
      <Form
        layout="vertical"
        initialValues={{ sql: column.dynamicSelectOptionConfig?.sql }}
        onFinish={(value: { sql: string }) => {
          setLoading(true)
          // ありえないはず
          if (config.type !== 'sheet' || config.fields === undefined || column.configIndex === undefined) {
            return
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const targetField = config.fields[column.configIndex]!
          const newTargetField = {
            ...targetField,
            override: {
              ...targetField.override,
              uiColumn: {
                ...targetField.override?.uiColumn,
                ...(value.sql === ''
                  ? { dynamicSelectOptionsConfig: undefined }
                  : {
                      dynamicSelectOptionsConfig: {
                        sql: value.sql,
                        type: 'sqlArray' as const,
                        valuesColumn: 'labels',
                      },
                    }),
              },
            },
          }
          const newFields = config.fields.map((x, index) => (index === column.configIndex ? newTargetField : x))
          // 設定=即保存としない(sheetに戻って意図したselectOptionが表示されているか確認するactionが想定されるため)
          setConfig({ ...config, fields: newFields })
          setHasChangeToViewSchema(true)
          setLoading(false)
          onAfterFinish()
        }}
      >
        {t(`SQLで連動項目の選択肢を設定することができます。`)}
        <DynamicSelectOptionSqlEditor />
        <Form.Item className="flex justify-end">
          <Button type="primary" htmlType="submit" loading={loading}>
            {t(`設定`)}
          </Button>
        </Form.Item>
      </Form>
    </PageHeader>
  )
}
